import { useState, type FormEvent } from 'react'
import { api, ApiError } from '@/lib/api'
import { useRouter } from '@/app/router'
import { Button, Field, Input } from '@/components/ui/primitives'
import { AuthFrame } from '@/features/auth/AuthFrame'

const MIN_LENGTH = 6

export function ResetPasswordPage() {
  const { query, navigate } = useRouter()
  const token = query.get('token') ?? ''
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    if (password !== confirm) {
      setError('The two passwords do not match.')
      return
    }

    setBusy(true)
    try {
      await api.post('/auth/reset-password', { token, new_password: password })
      setDone(true)
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Could not set the password. Request a new link and try again.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthFrame subtitle="Guest registration">
      {done ? (
        <>
          <h1 className="text-[20px] font-semibold tracking-[-0.01em]">Password set</h1>
          <p className="mt-1 text-[13px] text-[var(--text-muted)]">
            You can sign in now. Any other sessions were signed out.
          </p>
          <Button variant="primary" className="mt-5 w-full" onClick={() => navigate('/')}>
            Go to sign in
          </Button>
        </>
      ) : !token ? (
        <>
          <h1 className="text-[20px] font-semibold tracking-[-0.01em]">That link is incomplete</h1>
          <p className="mt-1 text-[13px] text-[var(--text-muted)]">
            Open the link from your email exactly as it was sent, or request a new one.
          </p>
          <Button variant="secondary" className="mt-5 w-full" onClick={() => navigate('/')}>
            Back to sign in
          </Button>
        </>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div>
            <h1 className="text-[20px] font-semibold tracking-[-0.01em]">Choose a password</h1>
            <p className="mt-1 text-[13px] text-[var(--text-muted)]">
              At least {MIN_LENGTH} characters. Length matters far more than punctuation, so a
              short phrase you will remember beats a scramble you will write down.
            </p>
          </div>

          <Field label="New password" htmlFor="new-password" required>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              minLength={MIN_LENGTH}
              autoFocus
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              invalid={Boolean(error)}
              required
            />
          </Field>

          <Field label="Confirm password" htmlFor="confirm-password" required>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              invalid={Boolean(error)}
              required
            />
          </Field>

          {error && (
            <p role="alert" className="text-[13px] text-[var(--danger)]">
              {error}
            </p>
          )}

          <Button
            type="submit"
            variant="primary"
            loading={busy}
            disabled={password.length < MIN_LENGTH}
          >
            Set password
          </Button>
        </form>
      )}
    </AuthFrame>
  )
}
